/**
 * Shared colour tokens for QA Reports + Dashboard history chart.
 *
 * One entry per logical metric. Card icons, card accents, and chart lines
 * all read from here so a given metric always renders in the same colour
 * across the page.
 *
 * The token shape:
 * - `hsl`: raw HSL string (no wrapper) used by the chart's CSS-variable
 *   colour pipeline — e.g. `stroke="hsl(25 95% 53%)"`.
 * - `text`: Tailwind class for icon/text use.
 * - `bg`: Tailwind class for a soft accent chip behind the icon.
 * - `ring`: Tailwind class for the card's left-edge accent bar.
 *
 * When adding a new metric card, prefer reusing one of these entries or
 * extending this map. Do not hand-tune colours in individual components —
 * that's what caused the chart and the card to drift before.
 */
export type MetricKey =
  | 'ordersShipped'
  | 'totalOrders'
  | 'itemsProcessed'
  | 'itemsPrinted'
  | 'batchesCompleted'
  | 'avgUnitsPerOrder'
  | 'defects'
  | 'defectRate'
  | 'printIssues';

export interface MetricColor {
  hsl: string;         // "H S% L%" — composed into `hsl(...)` by chart config
  text: string;        // icon / text colour
  bg: string;          // soft chip background (~10% alpha)
  ring: string;        // card left-edge accent bar
}

export const METRIC_COLORS: Record<MetricKey, MetricColor> = {
  ordersShipped: {
    hsl: '152 76% 40%',
    text: 'text-emerald-600 dark:text-emerald-400',
    bg:   'bg-emerald-50 dark:bg-emerald-950/40',
    ring: 'before:bg-emerald-500',
  },
  totalOrders: {
    hsl: '239 84% 60%',
    text: 'text-indigo-600 dark:text-indigo-400',
    bg:   'bg-indigo-50 dark:bg-indigo-950/40',
    ring: 'before:bg-indigo-500',
  },
  itemsProcessed: {
    hsl: '258 90% 60%',
    text: 'text-violet-600 dark:text-violet-400',
    bg:   'bg-violet-50 dark:bg-violet-950/40',
    ring: 'before:bg-violet-500',
  },
  itemsPrinted: {
    hsl: '25 95% 53%',
    text: 'text-orange-600 dark:text-orange-400',
    bg:   'bg-orange-50 dark:bg-orange-950/40',
    ring: 'before:bg-orange-500',
  },
  batchesCompleted: {
    hsl: '187 77% 45%',
    text: 'text-cyan-600 dark:text-cyan-400',
    bg:   'bg-cyan-50 dark:bg-cyan-950/40',
    ring: 'before:bg-cyan-500',
  },
  avgUnitsPerOrder: {
    hsl: '217 91% 60%',
    text: 'text-blue-600 dark:text-blue-400',
    bg:   'bg-blue-50 dark:bg-blue-950/40',
    ring: 'before:bg-blue-500',
  },
  defects: {
    hsl: '0 72% 51%',
    text: 'text-rose-600 dark:text-rose-400',
    bg:   'bg-rose-50 dark:bg-rose-950/40',
    ring: 'before:bg-rose-500',
  },
  defectRate: {
    hsl: '215 20% 50%',
    text: 'text-slate-600 dark:text-slate-400',
    bg:   'bg-slate-100 dark:bg-slate-800/60',
    ring: 'before:bg-slate-500',
  },
  printIssues: {
    hsl: '33 96% 50%',
    text: 'text-amber-600 dark:text-amber-400',
    bg:   'bg-amber-50 dark:bg-amber-950/40',
    ring: 'before:bg-amber-500',
  },
};
